import { type TextareaProps } from "@/components/podkit/forms/Textarea";
import TextareaWithSuggestions, { type CommandSuggestion } from "@/components/podkit/forms/TextareaWithSuggestions";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useMembership } from "@/hooks/use-membership";
import type { AvailableCommand } from "@/hooks/use-stream-conversation";
import { useCommandPromptsInfinite, type PlainPrompt, type PlainAgentExecution } from "@/queries/agent-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useCompleteSupportBundle } from "@/hooks/use-complete-support-bundle";
import { useAllAgentExecutionsForEnvironment } from "@/queries/agent-queries";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useDebounce } from "use-debounce";
import { SEARCH_DEBOUNCE_MS } from "@/utils/debounce";

// Stub used when environment data hasn't loaded yet. The hook safely handles
// undefined status (supportBundleUrl will be falsy, so environment.id is never accessed).
const EMPTY_ENVIRONMENT = { id: "", status: undefined } as unknown as PlainEnvironment;

type Props = {
    agentExecution?: PlainAgentExecution;
    onSuggestionSelect?: (suggestion: CommandSuggestion) => boolean;
    triggerChar?: string;
    popoverSideOffset?: number;
    popoverSide?: "top" | "bottom";
    popoverWidthOffset?: number;
    popoverAlignOffset?: number;
    showBuiltInCommands?: boolean | string[]; // true = all, false = none, array = specific commands
    /** Workspace-level commands from the AvailableCommands response block */
    availableCommands?: AvailableCommand[];
    /** When set, shows this message instead of command suggestions when trigger char is typed */
    disabledMessage?: string;
} & TextareaProps &
    PropsWithClassName;

const ChatInputWithPrompts = forwardRef<HTMLTextAreaElement, Props>(
    (
        {
            agentExecution,
            onSuggestionSelect,
            triggerChar = "/",
            popoverSideOffset,
            popoverSide,
            popoverWidthOffset,
            popoverAlignOffset,
            showBuiltInCommands = true,
            availableCommands,
            disabledMessage,
            className,
            ...textareaProps
        },
        ref,
    ) => {
        const [searchQuery, setSearchQuery] = useState("");
        const [debouncedSearch] = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS, { trailing: true });

        const { data: promptPages, fetchNextPage, hasNextPage, isFetchingNextPage } = useCommandPromptsInfinite(debouncedSearch);

        const allPrompts = useMemo(
            () => promptPages?.pages.flatMap((page) => page.prompts) ?? [],
            [promptPages],
        );

        const { membership } = useMembership();
        const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

        // Derive environment from agent execution for support bundle availability.
        const environmentId = agentExecution?.status?.usedEnvironments?.[0]?.environmentId;
        const { data: environment } = useEnvironment(environmentId);
        const { data: allExecutions } = useAllAgentExecutionsForEnvironment(environmentId);
        const { isSupportBundleAvailable, enableTokenFetch } = useCompleteSupportBundle(environment ?? EMPTY_ENVIRONMENT, allExecutions);

        // Eagerly enable token fetch for command suggestion display.
        useEffect(() => {
            enableTokenFetch();
        }, [enableTokenFetch]);

        const handleSearchChange = useCallback((query: string) => {
            setSearchQuery(query);
        }, []);

        const handleLoadMore = useCallback(() => {
            if (hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
            }
        }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

        // Convert prompts to suggestions and add built-in commands
        const suggestions: CommandSuggestion[] = useMemo(() => {
            const builtInCommands: CommandSuggestion[] = [];

            // Determine which built-in commands to show
            const shouldShowCommand = (commandName: string): boolean => {
                if (showBuiltInCommands === false) return false;
                if (showBuiltInCommands === true) return true;
                // Support both /skills and /commands for backward compatibility
                if (Array.isArray(showBuiltInCommands)) {
                    if (showBuiltInCommands.includes(commandName)) return true;
                    // If looking for /skills, also check for /commands and vice versa
                    if (commandName === "/skills" && showBuiltInCommands.includes("/commands")) return true;
                    if (commandName === "/commands" && showBuiltInCommands.includes("/skills")) return true;
                }
                return false;
            };

            // Add /clear command
            if (shouldShowCommand("/clear")) {
                builtInCommands.push({
                    value: "/clear",
                    label: "Reset conversation",
                    description: "Clear the chat history and start a new conversation",
                    id: "builtin-clear",
                });
            }

            // Add support bundle command
            if (shouldShowCommand("/support-bundle")) {
                builtInCommands.push({
                    value: "/support-bundle",
                    label: "Download support bundle",
                    description: isSupportBundleAvailable
                        ? "Download a support bundle for debugging"
                        : "Download a support bundle (currently unavailable)",
                    id: "builtin-support-bundle",
                });
            }

            // Show /skills as the primary suggestion; /commands is accepted as a silent alias
            if ((shouldShowCommand("/skills") || shouldShowCommand("/commands")) && isOrgAdmin) {
                builtInCommands.push({
                    value: "/skills",
                    label: "Manage skills",
                    description: "Go to the Agents settings page to manage skills",
                    id: "builtin-skills",
                });
            }

            const promptSuggestions: CommandSuggestion[] = allPrompts
                .filter((prompt: PlainPrompt) => prompt.spec?.command) // Only prompts with commands
                .map((prompt: PlainPrompt) => ({
                    value: `/${prompt.spec?.command}`,
                    label: prompt.metadata?.name || `/${prompt.spec?.command}`,
                    description: prompt.metadata?.description,
                    id: prompt.id,
                }));

            const workspaceSuggestions: CommandSuggestion[] = (availableCommands || [])
                .map((cmd) => ({
                    value: `/${cmd.name}`,
                    label: cmd.name,
                    description: cmd.description,
                    id: `workspace-${cmd.name}`,
                    group: "Environment",
                }))
                .sort((a, b) => a.value.localeCompare(b.value));

            const nonGroupedSuggestions = [...builtInCommands, ...promptSuggestions];

            // Sort non-workspace suggestions by command length (shortest first), then alphabetically
            nonGroupedSuggestions.sort((a, b) => {
                const lengthDiff = a.value.length - b.value.length;
                if (lengthDiff !== 0) {
                    return lengthDiff;
                }
                return a.value.localeCompare(b.value);
            });

            return [...nonGroupedSuggestions, ...workspaceSuggestions];
        }, [allPrompts, isOrgAdmin, showBuiltInCommands, isSupportBundleAvailable, availableCommands]);

        return (
            <TextareaWithSuggestions
                ref={ref}
                suggestions={suggestions}
                onSuggestionSelect={onSuggestionSelect}
                triggerChar={triggerChar}
                popoverWidthOffset={popoverWidthOffset}
                popoverSideOffset={popoverSideOffset}
                popoverSide={popoverSide}
                popoverAlignOffset={popoverAlignOffset}
                disabledMessage={disabledMessage}
                onSearchChange={handleSearchChange}
                onLoadMore={handleLoadMore}
                hasMore={hasNextPage}
                isLoadingMore={isFetchingNextPage}
                className={cn(
                    "w-full max-w-[700px] resize-none rounded-none border-0 bg-transparent p-0 focus-within:ring-0 focus-visible:ring-0",
                    className,
                )}
                {...textareaProps}
            />
        );
    },
);

ChatInputWithPrompts.displayName = "ChatInputWithPrompts";

export default ChatInputWithPrompts;
